import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * Client-seitige Redirects von alten WordPress-URLs (aparthotel-lerch.at)
 * auf die neue Seitenstruktur. SPA = `replace`-Navigate (Crawler folgen JS).
 *
 * EN-Alt-URLs werden auf die neue (deutsche) Route umgeleitet und gleichzeitig
 * die Locale per `?lang=en` + localStorage auf Englisch gesetzt.
 */

// Statische DE-Redirects (alte Trailing-Slash-Pfade)
export const LEGACY_REDIRECTS: Record<string, string> = {
  "/ferienwohnungen-kappl": "/suites",
  "/ferienwohnungen-kappl/garten-suite": "/suites/garten-suite",
  "/ferienwohnungen-kappl/austria-suite": "/suites/austria-suite",
  "/ferienwohnungen-kappl/swiss-suite": "/suites/swiss-suite",
  "/ferienwohnungen-kappl/deluxe-suite": "/suites/deluxe-suite",
  "/ferienwohnungen-kappl/wissenswertes": "/lerch-plus",
  "/ferienwohnungen-kappl/pauschalen": "/lerch-plus",
  "/kappl": "/lage-anreise",
  "/kappl-im-winter": "/erlebnisse/winter",
  "/kappl-im-sommer": "/erlebnisse/sommer",
  "/paznaun-im-winter": "/erlebnisse/winter",
  "/paznaun-im-sommer": "/erlebnisse/sommer",
  "/anfragen": "/anfrage",
  "/kontakt-lage": "/lage-anreise",
};

// EN-Alt-URLs -> neue Routen (Locale wird auf EN gesetzt)
export const LEGACY_EN_REDIRECTS: Record<string, string> = {
  "/en": "/",
  "/en/booking": "/buchen",
  "/en/request": "/anfrage",
  "/en/apartments": "/suites",
  "/en/apartments/garden-suite": "/suites/garten-suite",
  "/en/apartments/austria-suite": "/suites/austria-suite",
  "/en/apartments/swiss-suite": "/suites/swiss-suite",
  "/en/apartments/deluxe-suite": "/suites/deluxe-suite",
  "/en/apartments/valuable-information": "/lerch-plus",
  "/en/apartments/packages": "/lerch-plus",
  "/en/wellness": "/wellness",
  "/en/kappl": "/lage-anreise",
  "/en/kappl-in-winter": "/erlebnisse/winter",
  "/en/kappl-in-summer": "/erlebnisse/sommer",
  "/en/paznaun-im-sommer": "/erlebnisse/sommer",
  "/en/paznaun-in-winter": "/erlebnisse/winter",
  "/en/contact-location": "/lage-anreise",
  "/en/site-notice": "/impressum",
  "/en/privacy-policy": "/datenschutz",
};

/** Suchen den passenden Redirect für den aktuellen Pfad (mit/ohne Trailing-Slash). */
export function lookupLegacyRedirect(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return LEGACY_REDIRECTS[normalized] ?? LEGACY_EN_REDIRECTS[normalized] ?? null;
}

/** Generische Redirect-Komponente. Wenn `lang="en"`, wird die Locale auf EN gesetzt. */
export const Redirect: React.FC<{ to: string; lang?: "de" | "en" }> = ({ to, lang }) => {
  const location = useLocation();
  useEffect(() => {
    if (lang === "en") {
      try {
        window.localStorage.setItem("locale", "en");
      } catch {
        /* ignore */
      }
    }
  }, [lang]);

  const params = new URLSearchParams(location.search);
  if (lang === "en") params.set("lang", "en");
  const search = params.toString();
  return <Navigate to={`${to}${search ? `?${search}` : ""}`} replace />;
};

/** Anfrage-Redirect mit catId-Mapping (alte WP-Kategorie-IDs -> Suite-Slug). */
const CAT_ID_TO_SLUG: Record<string, string> = {
  "11": "deluxe-suite",
  "12": "swiss-suite",
  "13": "garten-suite",
  "14": "austria-suite",
};

export const AnfrageLegacyRedirect: React.FC<{ lang?: "de" | "en" }> = ({ lang }) => {
  const location = useLocation();
  useEffect(() => {
    if (lang === "en") {
      try {
        window.localStorage.setItem("locale", "en");
      } catch {
        /* ignore */
      }
    }
  }, [lang]);

  const params = new URLSearchParams(location.search);
  const catId = params.get("catId");
  const slug = catId ? CAT_ID_TO_SLUG[catId] : null;
  const out = new URLSearchParams();
  if (slug) out.set("suite", slug);
  if (lang === "en") out.set("lang", "en");
  const search = out.toString();
  return <Navigate to={`/anfrage${search ? `?${search}` : ""}`} replace />;
};
